import { MelodyLib, referenceSoundsIntoOpenMic, type MelodyTrainerMode, type PracticeLevel } from '@tunasong/music-lib'

/**
 * Whether an attempt's score is withheld because the tool may have measured its own playback.
 *
 * `detectBleed` answers "could a voice have produced these numbers?". This answers the question
 * the trainer actually has to act on, which is a different one: "was there anything for the
 * microphone to hear?". The two are separate because the second is a fact about the SESSION and
 * not about the take - at Level 2 nothing sounds during the capture, so a take that matches the
 * reference exactly is simply note-perfect, and withholding its score would be the tool refusing
 * to believe a singer who got it right. MIDI is the same argument from the other end: no
 * microphone, nothing to bleed.
 *
 * It lives beside the trainer rather than in `music-lib` because it composes a `melody` predicate
 * with an `analysis` one, and `melody` cannot import `analysis` without closing a cycle.
 *
 * Spec: docs/superpowers/specs/2026-09-08-melody-plugin-design.md §12.2
 */

export interface BleedGateInput {
  level: PracticeLevel
  mode: MelodyTrainerMode
  /** Per-paired-note deviation from the reference, from `scorePhrase`. */
  perNote: MelodyLib.BleedNote[]
  /** Per-paired-note IQR in cents, index-aligned with `perNote`. */
  stability: number[]
}

export const shouldWithholdScore = (input: BleedGateInput): boolean =>
  referenceSoundsIntoOpenMic({ level: input.level, mode: input.mode }) &&
  MelodyLib.detectBleed(input.perNote, input.stability)
